import React, { useState, useCallback, useEffect, useRef } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  useMediaQuery,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker';
import { PickersDay } from '@mui/x-date-pickers/PickersDay';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
  subMonths,
  isWithinInterval,
  isSameDay,
  isAfter,
  isBefore,
  setHours,
  setMinutes,
  startOfMinute,
  endOfMinute
} from 'date-fns';
import dateFormat from 'dateformat';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import {
  Container,
  SelectButton,
  ButtonContent,
  ButtonLabel,
  DatePickerContainer,
  DatePickerWrapper,
  DatePickerLabel,
  RangeDay,
  RangeDayStart,
  RangeDayEnd,
  RangeDayMiddle,
  TimeSelectContainer,
  TimeColon,
  MobileDatePickerDivider,
  DialogCloseButton
} from './styles';
import { momentFormat } from '#helper';

const dateFromStoredIso = value => (value ? new Date(value) : null);

const rangeToStoredIsos = (rangeStart, rangeEnd) => {
  const actualStart = isBefore(rangeStart, rangeEnd) ? rangeStart : rangeEnd;
  const actualEnd = isAfter(rangeStart, rangeEnd) ? rangeStart : rangeEnd;

  return {
    start: dateFormat(startOfMinute(actualStart), 'isoUtcDateTime'),
    end: dateFormat(endOfMinute(actualEnd), 'isoUtcDateTime')
  };
};

const getDefaultStartDate = () => {
  const date = subMonths(new Date(), 3);
  date.setHours(0, 0, 0, 0);
  return date;
};

const getDefaultEndDate = () => {
  const date = new Date();
  date.setHours(23, 59, 0, 0);
  return date;
};

const CustomDay = ({ day, startDate, endDate, ...other }) => {
  const isStart = startDate && isSameDay(day, startDate);
  const isEnd = endDate && isSameDay(day, endDate);
  const isInRange = startDate && endDate &&
    isWithinInterval(day, {
      start: isBefore(startDate, endDate) ? startDate : endDate,
      end: isAfter(startDate, endDate) ? startDate : endDate
    });
  const isMiddle = isInRange && !isStart && !isEnd;

  if (isStart && isEnd) {
    return <RangeDay {...other} day={day} />;
  }

  if (isStart) {
    return <RangeDayStart {...other} day={day} />;
  }

  if (isEnd) {
    return <RangeDayEnd {...other} day={day} />;
  }

  if (isMiddle) {
    return <RangeDayMiddle {...other} day={day} />;
  }

  return <PickersDay {...other} day={day} />;
};

const RangeDatePicker = ({
  value,
  onDateChange,
  maxDate,
  startDate,
  endDate,
  label
}) => (
  <DatePickerWrapper>
    {label && <DatePickerLabel>{label}</DatePickerLabel>}
    <StaticDatePicker
      displayStaticWrapperAs="desktop"
      value={value}
      onChange={onDateChange}
      maxDate={maxDate}
      slots={{
        day: CustomDay
      }}
      slotProps={{
        actionBar: { actions: [] },
        day: { startDate, endDate }
      }}
    />
  </DatePickerWrapper>
);

export const DateRangeButton = ({
  relatedTo,
  onChange,
  label = 'Select date range'
}) => {
  const theme = useTheme();
  const account = useSelector(s => s.account, shallowEqual);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpen, setIsOpen] = useState(false);
  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [activeTimePicker, setActiveTimePicker] = useState(null);
  const [tempTime, setTempTime] = useState(null);
  const [pendingDate, setPendingDate] = useState(null);
  const applyBaselineRef = useRef(null);
  const filterState = useSelector(s => s.listFilter?.[relatedTo], shallowEqual);

  const storedStart = filterState?.start?.value;
  const storedEnd = filterState?.end?.value;

  const [startDate, setStartDate] = useState(() => dateFromStoredIso(storedStart));
  const [endDate, setEndDate] = useState(() => dateFromStoredIso(storedEnd));

  useEffect(() => {
    if (storedStart) {
      setStartDate(dateFromStoredIso(storedStart));
    } else {
      setStartDate(null);
    }
    if (storedEnd) {
      setEndDate(dateFromStoredIso(storedEnd));
    } else {
      setEndDate(null);
    }
  }, [storedStart, storedEnd]);

  const handleOpen = () => {
    let s = startDate;
    let e = endDate;
    if (!s) {
      s = getDefaultStartDate();
      setStartDate(s);
    }
    if (!e) {
      e = getDefaultEndDate();
      setEndDate(e);
    }
    applyBaselineRef.current = s && e ? rangeToStoredIsos(s, e) : null;
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleStartDateChange = newDate => {
    if (newDate) {
      setPendingDate(newDate);
      setActiveTimePicker('start');
      const minutes = startDate?.getMinutes() || 0;
      const hours = startDate?.getHours() || 0;
      setTempTime(setHours(setMinutes(newDate, minutes), hours));
      setTimeDialogOpen(true);
    }
  };

  const handleEndDateChange = newDate => {
    if (newDate) {
      setPendingDate(newDate);
      setActiveTimePicker('end');
      const minutes = endDate?.getMinutes() || 59;
      const hours = endDate?.getHours() || 23;
      setTempTime(setHours(setMinutes(newDate, minutes), hours));
      setTimeDialogOpen(true);
    }
  };

  const handleTimeDialogClose = () => {
    setTimeDialogOpen(false);
    setActiveTimePicker(null);
    setTempTime(null);
    setPendingDate(null);
  };

  const handleHourChange = event => {
    if (tempTime) {
      const newTime = setHours(tempTime, parseInt(event.target.value, 10));
      setTempTime(newTime);
    }
  };

  const handleMinuteChange = event => {
    if (tempTime) {
      const newTime = setMinutes(tempTime, parseInt(event.target.value, 10));
      setTempTime(newTime);
    }
  };

  const handleTimeConfirm = () => {
    if (tempTime && pendingDate) {
      const updated = setHours(
        setMinutes(pendingDate, tempTime.getMinutes()),
        tempTime.getHours()
      );

      if (activeTimePicker === 'start') {
        setStartDate(updated);
        if (endDate && isAfter(updated, endDate)) {
          setEndDate(updated);
        }
      } else if (activeTimePicker === 'end') {
        setEndDate(updated);
        if (startDate && isBefore(updated, startDate)) {
          setStartDate(updated);
        }
      }
    }
    handleTimeDialogClose();
  };

  const handleApply = useCallback(() => {
    if (startDate && endDate) {
      const next = rangeToStoredIsos(startDate, endDate);
      const baseline = applyBaselineRef.current;
      const unchangedSinceOpen = baseline &&
        next.start === baseline.start &&
        next.end === baseline.end;

      if (!(unchangedSinceOpen && storedStart && storedEnd) &&
        (next.start !== storedStart || next.end !== storedEnd)) {
        onChange(next.start, 'start', relatedTo);
        onChange(next.end, 'end', relatedTo);
      }
    }
    setIsOpen(false);
  }, [startDate, endDate, onChange, relatedTo, storedStart, storedEnd]);

  const handleClear = useCallback(() => {
    setStartDate(null);
    setEndDate(null);
    onChange(null, 'start', relatedTo);
    onChange(null, 'end', relatedTo);
    setIsOpen(false);
  }, [onChange, relatedTo]);

  const hasDateRange = storedStart && storedEnd;

  const getDisplayLabel = () => {
    if (!hasDateRange) return label;

    const startFormatted = momentFormat(dateFromStoredIso(storedStart), account, 'dateTimeShort');
    const endFormatted = momentFormat(dateFromStoredIso(storedEnd), account, 'dateTimeShort');

    return `${startFormatted} - ${endFormatted}`;
  };

  const timeDialogTitle = activeTimePicker === 'start' ? 'Select Start Time' : 'Select End Time';

  return (
    <Container>
      <SelectButton onClick={handleOpen}>
        <ButtonContent>
          <CalendarTodayIcon fontSize="small" />
          <ButtonLabel>{getDisplayLabel()}</ButtonLabel>
        </ButtonContent>
      </SelectButton>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="md"
        fullScreen={isMobile}
        fullWidth={isTablet}
        PaperProps={{
          style: isMobile ? {} : { maxWidth: 700 }
        }}
      >
        <DialogCloseButton onClick={handleClose} aria-label="close">
          <CloseIcon />
        </DialogCloseButton>
        <DialogContent>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePickerContainer>
              <RangeDatePicker
                value={startDate}
                onDateChange={handleStartDateChange}
                maxDate={new Date()}
                startDate={startDate}
                endDate={endDate}
                label={isTablet ? 'Start Date' : null}
              />
              {isTablet && <MobileDatePickerDivider />}
              <RangeDatePicker
                value={endDate}
                onDateChange={handleEndDateChange}
                maxDate={new Date()}
                startDate={startDate}
                endDate={endDate}
                label={isTablet ? 'End Date' : null}
              />
            </DatePickerContainer>
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClear} color="inherit">
            Clear
          </Button>
          {!isMobile && (
            <Button onClick={handleClose} color="inherit">
              Cancel
            </Button>
          )}
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={!startDate || !endDate}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={timeDialogOpen}
        onClose={handleTimeDialogClose}
        maxWidth="xs"
        fullWidth={isMobile}
        PaperProps={{
          style: { minWidth: isMobile ? 'auto' : 300 }
        }}
      >
        <DialogTitle>{timeDialogTitle}</DialogTitle>
        <DialogContent>
          <TimeSelectContainer>
            <TextField
              select
              label="Hour"
              value={tempTime ? tempTime.getHours() : 0}
              onChange={handleHourChange}
              size="small"
              SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 200 } } } }}
            >
              {Array.from({ length: 24 }, (_, i) => (
                <MenuItem key={i} value={i}>
                  {i.toString().padStart(2, '0')}
                </MenuItem>
              ))}
            </TextField>
            <TimeColon>:</TimeColon>
            <TextField
              select
              label="Minute"
              value={tempTime ? tempTime.getMinutes() : 0}
              onChange={handleMinuteChange}
              size="small"
              SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 200 } } } }}
            >
              {Array.from({ length: 60 }, (_, i) => (
                <MenuItem key={i} value={i}>
                  {i.toString().padStart(2, '0')}
                </MenuItem>
              ))}
            </TextField>
          </TimeSelectContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleTimeDialogClose} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleTimeConfirm} variant="contained">
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
